<template>
  <div class="lab-exam-report-preview-container">
    <!-- 空資料提示 -->
    <div v-if="!hasReports" class="empty-state">
      <i class="fa fa-file-text-o empty-icon"></i>
      <p>尚未載入或無法解析檢驗數據</p>
    </div>

    <template v-else>
      <!-- 各設備檢驗報告卡片清單 (純檢驗數據) -->
      <div 
        v-for="(report, idx) in formattedReports" 
        :key="`report-${report.exam_device_id || idx}-${idx}`" 
        class="lab-report-card"
        :class="getDeviceThemeClass(report.device_name)"
      >
        <div class="device-header">
          <div class="device-title-box">
            <span class="device-badge">{{ report.device_name }}</span>
            <span class="device-type-tag" v-if="report.device_type">{{ report.device_type }}</span>
            <span class="test-time" v-if="report.exam_datetime">檢驗時間：{{ report.exam_datetime }}</span>
            <span class="page-badge" v-if="report.page_number">第 {{ report.page_number }} 頁</span>
          </div>
          <div class="device-actions">
            <button class="btn-copy" @click="handleCopy(JSON.stringify(report, null, 2))">複製此設備 JSON</button>
          </div>
        </div>

        <div class="table-responsive">
          <table class="lab-table">
            <thead>
              <tr>
                <th style="width: 70px;" class="text-center">序號</th>
                <th>檢驗項目 (Item)</th>
                <th>檢驗數值 (Value)</th>
                <th>單位 (Unit)</th>
                <th>參考區間 (Reference)</th>
                <th>
                  歷史檢驗值
                  <span v-if="report.previous_datetime" class="history-time-tag">({{ report.previous_datetime }})</span>
                </th>
                <th>原始文字</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, itemIdx) in report.items" :key="`item-${itemIdx}`">
                <td class="text-center text-muted">{{ itemIdx + 1 }}</td>
                <td class="font-bold text-primary">{{ item.item }}</td>
                <td class="font-mono font-bold text-dark">{{ item.value !== undefined && item.value !== '' ? item.value : '-' }}</td>
                <td>
                  <span v-if="item.unit" class="unit-tag">{{ item.unit }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="font-mono">
                  <span v-if="item.reference_interval" class="ref-interval-text">{{ item.reference_interval }}</span>
                  <span v-else class="text-muted">未設定</span>
                </td>
                <td class="text-secondary font-mono">
                  <span v-if="item.previous_result" class="prev-val-text">{{ item.previous_result }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-muted text-small">{{ item.raw || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * 檢驗報告預覽元件
 * 接收標準 storageData JSON 格式，並負責解析與呈現專業醫療檢驗結果 (純檢驗數據)
 */
export default {
  name: 'LabExamReportPreview',
  props: {
    /**
     * 檢驗報告陣列 (exam_reports) 或包裝物件
     * @type {Array|Object}
     */
    storageData: {
      type: [Array, Object],
      default: () => []
    }
  },
  computed: {
    /**
     * 取得設備檢驗報告列表
     * @returns {Array}
     */
    formattedReports() {
      try {
        if (!this.storageData) return [];
        if (Array.isArray(this.storageData)) return this.storageData;
        if (Array.isArray(this.storageData.exam_reports)) return this.storageData.exam_reports;
        return [];
      } catch (error) {
        console.error('[LabExamReportPreview] formattedReports computed error:', error);
        return [];
      }
    },

    /**
     * 是否有任何報告
     * @returns {boolean}
     */
    hasReports() {
      return this.formattedReports.length > 0;
    }
  },
  methods: {
    /**
     * 取得設備主題 CSS Class
     * @param {string} deviceName - 設備名稱
     * @returns {string}
     */
    getDeviceThemeClass(deviceName) {
      if (!deviceName) return 'theme-default';
      const name = String(deviceName).toLowerCase();
      if (name.includes('procyte')) return 'theme-procyte';
      if (name.includes('catalyst')) return 'theme-catalyst';
      return 'theme-default';
    },

    /**
     * 複製文字至剪貼簿
     * @param {string} text - 欲複製文字
     */
    async handleCopy(text) {
      try {
        await navigator.clipboard.writeText(text);
        alert('已複製到剪貼簿！');
      } catch (error) {
        console.error('[LabExamReportPreview] handleCopy error:', error);
      }
    }
  }
};
</script>

<style scoped>
.lab-exam-report-preview-container {
  width: 100%;
}

/* 檢驗報告卡片樣式 */
.lab-report-card {
  margin-bottom: 24px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.lab-report-card.theme-procyte {
  border-top: 4px solid #0284c7;
}

.lab-report-card.theme-catalyst {
  border-top: 4px solid #d97706;
}

.device-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 12px;
}

.device-title-box {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.device-badge {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.theme-procyte .device-badge {
  color: #0284c7;
}

.theme-catalyst .device-badge {
  color: #d97706;
}

.device-type-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background: #e2e8f0;
  color: #475569;
}

.theme-procyte .device-type-tag {
  background: #e0f2fe;
  color: #0369a1;
}

.theme-catalyst .device-type-tag {
  background: #fef3c7;
  color: #b45309;
}

.test-time {
  font-size: 13px;
  color: #64748b;
  background: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.page-badge {
  font-size: 12px;
  color: #64748b;
}

.table-responsive {
  overflow-x: auto;
}

.lab-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 14px;
}

.lab-table th {
  background-color: #f8fafc;
  color: #475569;
  font-weight: 600;
  padding: 10px 16px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
}

.lab-table td {
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.lab-table tr:hover {
  background-color: #f8fafc;
}

.unit-tag {
  display: inline-block;
  background-color: #e0f2fe;
  color: #0369a1;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.font-bold {
  font-weight: 600;
}

.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.text-primary {
  color: #2563eb;
}

.text-dark {
  color: #0f172a;
}

.text-secondary {
  color: #64748b;
}

.text-muted {
  color: #94a3b8;
}

.text-small {
  font-size: 12px;
}

.text-center {
  text-align: center;
}

.history-time-tag {
  display: inline-block;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  margin-left: 4px;
}

.ref-interval-text {
  color: #047857;
  font-weight: 600;
}

.prev-val-text {
  color: #475569;
}

.btn-copy {
  padding: 6px 12px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  color: #334155;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-copy:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
  font-size: 15px;
}

.empty-icon {
  font-size: 36px;
  margin-bottom: 12px;
  color: #cbd5e1;
}
</style>
